import {Box, Fade, keyframes, Tooltip, useTheme} from "@mui/material";
import {useRefreshIndicatorStore} from "./refreshIndicatorStore";
import {useEffect, useMemo, useState} from "react";

const pulse = keyframes`
    0% {
        box-shadow: 0 0 0 0 currentColor;
        opacity: 0.8;
    }
    50% {
        opacity: 0.5;
    }
    100% {
        box-shadow: 0 0 0 6px transparent;
        opacity: 0.8;
    }
`;

export function RefreshIndicator() {
    const theme = useTheme();
    const {requests, connectionStatus} = useRefreshIndicatorStore();
    const [visible, setVisible] = useState(false);

    const state: {
        tooltip: string;
        color: string;
        shouldShow: boolean;
        isAnimated: boolean;
    } = useMemo(() => {
        return connectionStatus === 'disconnected' ? {
            tooltip: 'No connection to server',
            color: theme.palette.error.main,
            shouldShow: true,
            isAnimated: false,
        } : connectionStatus === 'no-internet' ? {
            tooltip: 'No internet connection',
            color: theme.palette.warning.main,
            shouldShow: true,
            isAnimated: false,
        } : requests.size > 0 ? {
            tooltip: 'Refreshing data...',
            color: theme.palette.info.main,
            shouldShow: true,
            isAnimated: true,
        } : {
            tooltip: 'Connected',
            color: theme.palette.success.main,
            shouldShow: false,
            isAnimated: false,
        }
    }, [connectionStatus, requests, theme]);

    useEffect(() => {
        if (state.shouldShow) {
            const timer = setTimeout(() => setVisible(true), 200);
            return () => clearTimeout(timer);
        } else {
            setVisible(false);
        }
    }, [state.shouldShow]);

    return (
        <Fade in={visible}>
            <Tooltip title={state.tooltip} arrow placement="bottom-end">
                <Box
                    sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: state.color,
                        animation: state.isAnimated ? `${pulse} 1.5s ease-in-out infinite` : 'none',
                        marginRight: 1.5,
                    }}
                    role="status"
                    aria-label={state.tooltip}
                />
            </Tooltip>
        </Fade>
    );
}